// frontend/src/services/api/postgrest.js

import axios from "axios";
import {
  AUTH_TOKEN_HEADER_NAME,
  POSTGREST_URL,
  buildAuthHeaders,
  sanitizeAuthToken,
} from "../common";

const JSON_CONTENT_TYPE_HEADER = "Content-Type";
const JSON_ACCEPT_HEADER = "Accept";
const JSON_MEDIA_TYPE = "application/json";
const CLIENT_TIMEOUT_MS = 30000;
const LOGIN_ENDPOINT = "/rpc/login";
const LOGIN_EMAIL_FIELD = "p_email";
const LOGIN_PIN_FIELD = "p_pin";
const CURRENT_USER_ENDPOINT = "/rpc/current_user";
const LOGOUT_ENDPOINT = "/rpc/logout";
const EMPTY_STRING = "";
const POSTGREST_CLIENT_LOG_PREFIX = "[PostgREST API]";
const LOG_METHOD_REQUEST = "info";
const LOG_METHOD_RESPONSE = "info";
const LOG_METHOD_ERROR = "error";
const SENSITIVE_FIELDS = ["pin", LOGIN_PIN_FIELD];
const MASKED_VALUE = "******";

let sessionToken = EMPTY_STRING;

const buildLoginPayload = ({ email, pin }) => ({
  [LOGIN_EMAIL_FIELD]: email,
  [LOGIN_PIN_FIELD]: pin,
});

const sanitizePayload = (payload) => {
  if (payload === null || payload === undefined) {
    return payload;
  }

  if (Array.isArray(payload)) {
    return payload.map(sanitizePayload);
  }

  if (typeof payload !== "object") {
    return payload;
  }

  return Object.entries(payload).reduce((sanitized, [key, value]) => {
    sanitized[key] = SENSITIVE_FIELDS.includes(key) ? MASKED_VALUE : sanitizePayload(value);
    return sanitized;
  }, {});
};

const resolveRequestUrl = (config) => {
  if (!config) {
    return "";
  }

  const requestUrl = typeof config.url === "string" ? config.url : "";
  if (/^https?:\/\//i.test(requestUrl)) {
    return requestUrl;
  }

  const baseUrl = typeof config.baseURL === "string" ? config.baseURL : "";

  if (!baseUrl) {
    return requestUrl;
  }

  if (baseUrl.endsWith("/") && requestUrl.startsWith("/")) {
    return `${baseUrl}${requestUrl.slice(1)}`;
  }

  if (!baseUrl.endsWith("/") && !requestUrl.startsWith("/")) {
    return `${baseUrl}/${requestUrl}`;
  }

  return `${baseUrl}${requestUrl}`;
};

const logPostgrestEvent = (level, message, details) => {
  const logger = console[level] || console.log;
  logger(`${POSTGREST_CLIENT_LOG_PREFIX} ${message}`, details);
};

export const sanitizeToken = (token) => {
  return sanitizeAuthToken(token);
};

const createEmptyJsonBody = () => ({});

const mergeHeaders = (currentHeaders, nextHeaders) => {
  if (!Object.keys(nextHeaders).length) {
    return currentHeaders;
  }

  return {
    ...(currentHeaders ?? {}),
    ...nextHeaders,
  };
};

const buildRpcRequestConfig = (token) => {
  const headers = buildAuthHeaders(token ?? sessionToken);

  if (!Object.keys(headers).length) {
    return {};
  }

  return { headers };
};

const shouldAttachAuthHeader = (config) =>
  Boolean(config?.headers?.[AUTH_TOKEN_HEADER_NAME]);

export const getSessionToken = () => sessionToken;

export const setSessionToken = (token) => {
  sessionToken = sanitizeAuthToken(token);
  return sessionToken;
};

export const clearSessionToken = () => {
  sessionToken = EMPTY_STRING;
  return sessionToken;
};

export const postgrestClient = axios.create({
  baseURL: POSTGREST_URL,
  timeout: CLIENT_TIMEOUT_MS,
  headers: {
    [JSON_CONTENT_TYPE_HEADER]: JSON_MEDIA_TYPE,
    [JSON_ACCEPT_HEADER]: JSON_MEDIA_TYPE,
  },
});

postgrestClient.interceptors.request.use(
  (config) => {
    const mergedHeaders = shouldAttachAuthHeader(config)
      ? config.headers
      : mergeHeaders(config?.headers, buildAuthHeaders(sessionToken));

    const enrichedConfig = {
      ...config,
      headers: mergedHeaders,
    };

    logPostgrestEvent(LOG_METHOD_REQUEST, "Requisição iniciada", {
      method: config?.method?.toUpperCase(),
      url: resolveRequestUrl(config),
      timeout: config?.timeout,
      payload: sanitizePayload(config?.data),
      headers: enrichedConfig?.headers,
    });

    return enrichedConfig;
  },
  (error) => {
    logPostgrestEvent(LOG_METHOD_ERROR, "Erro antes do envio da requisição", {
      message: error?.message,
      stack: error?.stack,
    });

    return Promise.reject(error);
  }
);

postgrestClient.interceptors.response.use(
  (response) => {
    logPostgrestEvent(LOG_METHOD_RESPONSE, "Resposta recebida", {
      method: response?.config?.method?.toUpperCase(),
      url: resolveRequestUrl(response?.config),
      status: response?.status,
      statusText: response?.statusText,
      data: sanitizePayload(response?.data),
      headers: response?.headers,
    });

    return response;
  },
  (error) => {
    if (error?.response) {
      logPostgrestEvent(LOG_METHOD_ERROR, "Resposta com erro", {
        method: error?.config?.method?.toUpperCase(),
        url: resolveRequestUrl(error?.config),
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: sanitizePayload(error?.response?.data),
        headers: error?.response?.headers,
      });
    } else {
      logPostgrestEvent(LOG_METHOD_ERROR, "Erro sem resposta do servidor", {
        url: resolveRequestUrl(error?.config),
        message: error?.message,
        code: error?.code,
      });
    }

    return Promise.reject(error);
  }
);

export const loginWithPin = ({ email, pin }) =>
  postgrestClient.post(LOGIN_ENDPOINT, buildLoginPayload({ email, pin }));

export const fetchCurrentUser = (token) => {
  return postgrestClient.post(
    CURRENT_USER_ENDPOINT,
    createEmptyJsonBody(),
    buildRpcRequestConfig(token)
  );
};

export const logoutSession = (token) =>
  postgrestClient.post(
    LOGOUT_ENDPOINT,
    createEmptyJsonBody(),
    buildRpcRequestConfig(token)
  );
